import gsap from "gsap";
import observe from "./observe";

const COLORS = ['#B7E245', '#94c11e', '#0067A9', '#FFD23F', '#F28C28'];
const PRICE = /\d[\d\s .,]*\d|\d/;

const popups = {
    ready: false,

    init() {
        this.ready = true;
        this.watch('.atc-popup-overlay', (overlay) => this.addedToCart(overlay));
        this.watch('.login-popup-overlay:not(.login-popup-inline)', (overlay) => this.dialog(overlay, overlay.querySelector('.login-popup')));
        this.watch('.sliding-cart', (cart) => this.slidingCart(cart));
    },

    watch(selector, onOpen) {
        document.querySelectorAll(selector).forEach((el) => {
            observe.classChange(el, 'active', (active) => {
                if (active) {
                    onOpen(el);
                }
            });
        });
    },

    dialog(overlay, box) {
        gsap.fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.35, ease: 'power1.out', overwrite: true, clearProps: 'opacity' });

        if (box) {
            gsap.fromTo(box, { opacity: 0, y: 70, scale: 0.9, rotationX: -14, transformPerspective: 1200, transformOrigin: '50% 0%' }, {
                opacity: 1,
                y: 0,
                scale: 1,
                rotationX: 0,
                duration: 0.8,
                ease: 'back.out(1.3)',
                overwrite: true,
                clearProps: 'opacity,transform,transformOrigin',
            });
        }
    },

    close(overlay, box, done) {
        if (!this.ready || !overlay) {
            done();
            return;
        }

        gsap.killTweensOf([overlay, box]);
        gsap.timeline({
            onComplete: () => {
                gsap.set([overlay, box], { clearProps: 'opacity,transform' });
                done();
            },
        })
            .to(box, { opacity: 0, y: 40, scale: 0.94, duration: 0.3, ease: 'power2.in' }, 0)
            .to(overlay, { opacity: 0, duration: 0.3, ease: 'power1.in' }, 0.1);
    },

    addedToCart(overlay) {
        const popup = overlay.querySelector('.atc-popup');

        this.dialog(overlay, popup);

        if (!popup) {
            return;
        }

        gsap.fromTo(popup.querySelectorAll('.atc-popup__header, .atc-popup__product-info, .atc-popup__related'), { opacity: 0, y: 24 }, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            delay: 0.2,
            clearProps: 'opacity,transform',
        });
        gsap.fromTo(popup.querySelectorAll('.atc-popup__cart-summary'), { opacity: 0, x: 60 }, {
            opacity: 1,
            x: 0,
            duration: 0.9,
            delay: 0.3,
            ease: 'back.out(1.3)',
            clearProps: 'opacity,transform',
        });
        gsap.fromTo(popup.querySelectorAll('.atc-popup__product-image'), { scale: 0.6, rotation: -8 }, {
            scale: 1,
            rotation: 0,
            duration: 0.9,
            delay: 0.35,
            ease: 'back.out(2)',
            clearProps: 'transform',
        });
        gsap.fromTo(popup.querySelectorAll('.atc-popup__btn'), { opacity: 0, scale: 0.8 }, {
            opacity: 1,
            scale: 1,
            duration: 0.7,
            stagger: 0.1,
            delay: 0.55,
            ease: 'back.out(2)',
            clearProps: 'opacity,transform',
        });

        popup.querySelectorAll('.atc-popup__summary-value').forEach((value, index) => this.countUp(value, 0.45 + index * 0.1));
        this.check(popup);
        gsap.delayedCall(0.4, () => {
            gsap.fromTo(popup.querySelectorAll('.atc-popup__related-product'), { opacity: 0, y: 40, rotationY: -25, transformPerspective: 900 }, {
                opacity: 1,
                y: 0,
                rotationY: 0,
                duration: 0.8,
                stagger: 0.08,
                ease: 'back.out(1.4)',
                clearProps: 'opacity,transform',
            });
        });
    },

    check(popup) {
        const check = popup.querySelector('.atc-popup__check-icon');

        if (!check) {
            return;
        }

        gsap.fromTo(check, { scale: 0, rotation: -90, transformOrigin: '50% 50%' }, {
            scale: 1,
            rotation: 0,
            duration: 0.8,
            delay: 0.3,
            ease: 'back.out(2.6)',
            clearProps: 'transform,transformOrigin',
        });

        const tick = check.querySelector('path');

        if (tick && typeof tick.getTotalLength === 'function') {
            const length = tick.getTotalLength();

            gsap.fromTo(tick, { strokeDasharray: length, strokeDashoffset: length }, {
                strokeDashoffset: 0,
                duration: 0.45,
                delay: 0.65,
                ease: 'power2.inOut',
                clearProps: 'strokeDasharray,strokeDashoffset',
            });
        }

        const ring = document.createElementNS('http://www.w3.org/2000/svg', 'circle');

        ring.setAttribute('cx', '16');
        ring.setAttribute('cy', '16');
        ring.setAttribute('r', '16');
        ring.setAttribute('fill', 'none');
        ring.setAttribute('stroke', '#94c11e');
        ring.setAttribute('stroke-width', '3');
        check.insertBefore(ring, check.firstChild);
        gsap.fromTo(ring, { attr: { r: 16 }, opacity: 0.9 }, {
            attr: { r: 40 },
            opacity: 0,
            duration: 1.1,
            delay: 0.55,
            ease: 'power2.out',
            repeat: 1,
            repeatDelay: 0.15,
            onComplete: () => ring.remove(),
        });

        gsap.delayedCall(0.5, () => this.confetti(popup, check));
    },

    confetti(popup, origin) {
        const box = popup.getBoundingClientRect();
        const from = origin.getBoundingClientRect();
        const x = from.left + from.width / 2 - box.left;
        const y = from.top + from.height / 2 - box.top;
        const count = 28;

        for (let i = 0; i < count; i++) {
            const piece = document.createElement('span');
            const angle = (Math.PI * 2 * i) / count + gsap.utils.random(-0.25, 0.25);
            const distance = gsap.utils.random(70, 170);

            piece.className = 'ex-confetti';
            piece.style.left = x + 'px';
            piece.style.top = y + 'px';
            piece.style.background = COLORS[i % COLORS.length];

            if (i % 3 === 0) {
                piece.style.borderRadius = '50%';
            }

            popup.appendChild(piece);
            gsap.timeline({ onComplete: () => piece.remove() })
                .fromTo(piece, { x: 0, y: 0, scale: 0, rotation: 0 }, {
                    x: Math.cos(angle) * distance,
                    y: Math.sin(angle) * distance * 0.75,
                    scale: gsap.utils.random(0.6, 1.3),
                    rotation: gsap.utils.random(-300, 300),
                    duration: 0.75,
                    ease: 'expo.out',
                })
                .to(piece, { y: '+=' + gsap.utils.random(50, 110), opacity: 0, duration: 0.9, ease: 'power1.in' }, 0.55);
        }
    },

    price(text) {
        const match = text.match(PRICE);

        if (!match) {
            return null;
        }

        const raw = match[0];
        const decimals = /[.,]\d{2}$/.test(raw) ? 2 : 0;
        const integer = decimals ? raw.slice(0, -3) : raw;

        return parseFloat(integer.replace(/\D/g, '') + (decimals ? '.' + raw.slice(-2) : ''));
    },

    countUp(el, delay, from = 0) {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        let node = walker.nextNode();

        while (node && !PRICE.test(node.nodeValue)) {
            node = walker.nextNode();
        }

        if (!node) {
            return;
        }

        const original = node.nodeValue;
        const match = original.match(PRICE);
        const raw = match[0];
        const decimals = /[.,]\d{2}$/.test(raw) ? 2 : 0;
        const decimalSign = decimals ? raw.charAt(raw.length - 3) : '';
        const integer = decimals ? raw.slice(0, -3) : raw;
        const group = (integer.match(/[\s .,]/) || [''])[0];
        const target = this.price(raw);
        const before = original.slice(0, match.index);
        const after = original.slice(match.index + raw.length);
        const state = { value: from };

        if (target === from || Number.isNaN(target)) {
            return;
        }

        const render = () => {
            const parts = state.value.toFixed(decimals).split('.');
            const whole = group ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, group) : parts[0];

            node.nodeValue = before + whole + (decimals ? decimalSign + parts[1] : '') + after;
        };

        render();
        gsap.to(state, {
            value: target,
            duration: 1.2,
            delay,
            ease: 'power2.out',
            onUpdate: render,
            onComplete: () => {
                node.nodeValue = original;
            },
        });
    },

    slidingCart(cart) {
        const items = cart.querySelectorAll('.sliding-cart__item, .sliding-cart__empty');

        gsap.fromTo(items, { opacity: 0, x: 60, rotationY: -20, transformPerspective: 900 }, {
            opacity: 1,
            x: 0,
            rotationY: 0,
            duration: 0.7,
            stagger: 0.07,
            delay: 0.15,
            ease: 'back.out(1.4)',
            overwrite: true,
            clearProps: 'opacity,transform',
        });
        gsap.fromTo(cart.querySelectorAll('.sliding-cart__footer, .sliding-cart__summary, .sliding-cart__buttons'), { opacity: 0, y: 30 }, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            delay: 0.35,
            stagger: 0.08,
            clearProps: 'opacity,transform',
        });
    },
};

export default popups;
